import _ from "lodash";

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  FieldDefinitionEnumsType,
  FieldDefinitionEnumType,
  FieldDefinitionFieldType,
  FieldImperativeHandle,
  FieldSizeType,
} from "@/components/@types";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { Values } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useColorScheme } from "@/hooks/useColorScheme";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { Skeleton } from "@/components/ui/skeleton";

import { typographyStyles } from "@/components/ui/typography";

type EnumOption = { key: string; value: string };

/**
 * How a read-only enum's chip is drawn: ``soft``, tinted in its colour; or
 * ``outlined``, on the page's ground in its text, edged as a divider and
 * rounded off as the chips beside it are, its colour's edge a dot where a
 * start adornment would go.
 */
export type FieldEnumVariant = "soft" | "outlined";

/** ``EnumColors`` only define ``light`` / ``dark``; a colour scheme may come as ``undefined``, ``system``, etc. */
function enumPaletteMode(
  colorScheme: string | undefined | null,
): "light" | "dark" {
  return colorScheme === "dark" ? "dark" : "light";
}

function FieldEnumValueInteractive({
  value,
  valueOptions,
  style,
  events,
  size = "md",
  isBorderVisible = true,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionEnumType<any>> & {
  isBorderVisible?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const { colorScheme } = useColorScheme();
  const paletteMode = enumPaletteMode(colorScheme);
  const badgeRef = useRef<HTMLDivElement>(null);
  const [isPopperOpen, setIsPopperOpenState] = useState(false);
  const enumOptions = (valueOptions?.enum || []) as EnumOption[];
  const stableTestId = (valueOptions as { testId?: string } | undefined)
    ?.testId;

  // Manage transient state locally
  const [transientValue, setTransientValue] = useState<string | undefined>(
    typeof value === "string" ? value : undefined,
  );

  const setIsPopperOpen = useCallback(
    (isOpen: boolean) => {
      setIsPopperOpenState(isOpen ?? false);
      if (!isOpen) {
        events?.onChangeField?.(transientValue);
        events?.onSubmitField?.();
      }
    },
    [events, transientValue],
  );

  // Sync transient state when value prop changes
  useEffect(() => {
    setTransientValue(typeof value === "string" ? value : undefined);
  }, [value]);

  const handleItemClick = useCallback(
    (option: EnumOption) => {
      // Commit immediately on click — same UX as ``FieldSelect``. Without
      // this, clicking an option only stages a transient value and the
      // commit only fires when the popper later closes via outside-press,
      // which makes the click feel non-functional (the visible state of
      // the underlying field doesn't change until you click away). Most
      // visibly: the lookup's "Return mode" field could not be switched
      // from ``one`` to ``list`` by clicking "List".
      setTransientValue(option.value);
      setIsPopperOpenState(false);
      events?.onChangeField?.(option.value);
      events?.onSubmitField?.();
    },
    [events],
  );

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        badgeRef.current?.focus();
        // Open the popper when focused
        setIsPopperOpen(true);
      },
      unsetFocus: () => {
        badgeRef.current?.blur();
        setIsPopperOpen(false);
      },
    }),
    [],
  );

  // Update badge content to use transient value
  const transientText =
    typeof transientValue === "string"
      ? transientValue.replace(/_/g, " ")
      : transientValue;
  const transientColorMap = valueOptions?.getColors?.(paletteMode);
  const transientColors = transientColorMap?.[
    typeof transientValue === "string"
      ? transientValue
      : String(transientValue ?? "")
  ] ?? {
    color: EnumColors.GREY_1[paletteMode].color,
    backgroundColor: EnumColors.GREY_1[paletteMode].backgroundColor,
    borderColor: EnumColors.GREY_1[paletteMode].borderColor,
  };

  const transientBadgeContent = transientText ? (
    <div
      ref={badgeRef}
      tabIndex={0}
      role="button"
      data-testid={
        stableTestId ??
        `field-enum-${typeof transientText === "string" ? transientText : "value"}`
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsPopperOpen(true);
        }
      }}
      style={{
        paddingTop: "1px",
        paddingBottom: "1px",
        ...((typographyStyles as Record<string, React.CSSProperties>)[
          `body-${size}`
        ] as React.CSSProperties),
        color: transientColors.color,
        border: isBorderVisible
          ? `1px solid ${transientColors.borderColor}`
          : "1px solid transparent",
        backgroundColor: transientColors.backgroundColor,
        borderRadius: "calc(var(--radius) - 4px)",
        maxWidth: "100%",
        width: "fit-content",
        overflow: "hidden",
        textOverflow: "ellipsis",
        alignItems: "baseline",
        flexWrap: "nowrap",
        textWrap: "nowrap",
        paddingLeft: "6px",
        paddingRight: "6px",
        cursor: "pointer",
        outline: "none",
      }}
    >
      {typeof transientText === "string"
        ? valueOptions?.keepsCase
          ? transientText
          : _.capitalize(transientText)
        : transientText}
    </div>
  ) : (
    <div
      ref={badgeRef}
      tabIndex={0}
      role="button"
      data-testid={stableTestId ?? "field-enum-empty"}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsPopperOpen(true);
        }
      }}
      style={{
        paddingTop: "1px",
        paddingBottom: "1px",
        ...((typographyStyles as Record<string, React.CSSProperties>)[
          `body-${size}`
        ] as React.CSSProperties),
        color: "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
        border: isBorderVisible
          ? `1px solid ${"var(--border)"}`
          : "1px solid transparent",
        backgroundColor: "transparent",
        borderRadius: "calc(var(--radius) - 4px)",
        paddingLeft: "6px",
        paddingRight: "6px",
        cursor: "pointer",
        fontStyle: "italic",
        outline: "none",
      }}
    >
      Select option...
    </div>
  );

  return (
    <div style={{ ...style }}>
      <Popper
        anchor={transientBadgeContent}
        placement="bottom-start"
        isClickable={true}
        isHoverable={false}
        isOpenState={[isPopperOpen, setIsPopperOpen]}
        // A block, as the chip read alone is: inline, it sat on a line of the
        // page's own taller text, and was taller to pick than to read.
        style={{ display: "block" }}
        floatingStyle={{
          maxHeight: "400px",
          overflowY: "auto",
        }}
      >
        <List<EnumOption>
          data={enumOptions}
          getRowKey={(option) => option.value}
          getIsChecked={(option) => option.value === transientValue}
          isMultiselect={false}
          isNullable={true}
          onItemClick={handleItemClick}
          size="sm"
        >
          <List.Items<EnumOption>
            children={(option: EnumOption) => (
              <List.Item
                value={option}
                data-testid={
                  stableTestId
                    ? `${stableTestId}-option-${option.value}`
                    : `field-enum-option-${option.value}`
                }
              >
                <div
                  style={{
                    paddingTop: "1px",
                    paddingBottom: "1px",
                    ...((
                      typographyStyles as Record<string, React.CSSProperties>
                    )[`body-${size}`] as React.CSSProperties),
                    color:
                      transientColorMap?.[option.value]?.color ||
                      EnumColors.GREY_1[paletteMode].color,
                    border: `1px solid ${transientColorMap?.[option.value]?.borderColor || EnumColors.GREY_1[paletteMode].borderColor}`,
                    backgroundColor:
                      transientColorMap?.[option.value]?.backgroundColor ||
                      EnumColors.GREY_1[paletteMode].backgroundColor,
                    borderRadius: "calc(var(--radius) - 4px)",
                    paddingLeft: "6px",
                    paddingRight: "6px",
                    // As wide as its word, like the chip it becomes.
                    width: "fit-content",
                  }}
                >
                  {typeof option.value === "string"
                    ? valueOptions?.keepsCase
                      ? option.value
                      : _.capitalize(option.value.replace(/_/g, " "))
                    : option.value}
                </div>
              </List.Item>
            )}
          />
        </List>
      </Popper>
    </div>
  );
}

function FieldEnumValue({
  value,
  valueOptions,
  style,
  events,
  size = "md",
  isBorderVisible = true,
  readOnly = false,
  variant = "soft",
  endDecorator,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionEnumType<any>> & {
  isBorderVisible?: boolean;
  readOnly?: boolean;
  variant?: FieldEnumVariant;
  /** After its word, inside the chip: a key that does something to it. */
  endDecorator?: React.ReactNode;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const layoutColors = useLayoutColors();
  const isOutlined = variant === "outlined";
  const text = typeof value === "string" ? value?.replace(/_/g, " ") : value;
  const { colorScheme } = useColorScheme();
  const paletteMode = enumPaletteMode(colorScheme);

  const colorMap = valueOptions?.getColors?.(paletteMode);
  const { color, backgroundColor, borderColor } = colorMap?.[
    typeof value === "string" ? value : String(value)
  ] ?? {
    color: EnumColors.GREY_1[paletteMode].color,
    backgroundColor: EnumColors.GREY_1[paletteMode].backgroundColor,
    borderColor: EnumColors.GREY_1[paletteMode].borderColor,
  };

  // If events are passed, use interactive component (unless display-only readOnly)
  if (events && !readOnly) {
    return (
      <FieldEnumValueInteractive
        value={value}
        valueOptions={valueOptions}
        style={style}
        events={events}
        size={size}
        isBorderVisible={isBorderVisible}
        ref={ref}
      />
    );
  }

  const label =
    typeof text === "string" && !valueOptions?.keepsCase
      ? _.capitalize(text)
      : text;
  const badgeContent = text ? (
    <div
      style={{
        paddingTop: "1px",
        paddingBottom: "1px",
        ...((typographyStyles as Record<string, React.CSSProperties>)[
          `body-${size}`
        ] as React.CSSProperties),
        color: isOutlined ? "var(--foreground)" : color,
        border: isOutlined
          ? `1px solid ${"var(--border)"}`
          : isBorderVisible
            ? `1px solid ${borderColor}`
            : "1px solid transparent",
        backgroundColor: isOutlined ? layoutColors.main : backgroundColor,
        // Outlined, it rounds off as the chips it sits among do.
        borderRadius: isOutlined
          ? "var(--radius)"
          : "calc(var(--radius) - 4px)",
        maxWidth: "100%",
        width: "fit-content",
        overflow: "hidden",
        textOverflow: "ellipsis",
        // On its word's baseline, outlined too - its dot centred on its own -
        // so the chip's baseline is its word's, and it sits on a line of text
        // beside it.
        alignItems: "baseline",
        flexWrap: "nowrap",
        textWrap: "nowrap",
        paddingLeft: "6px",
        paddingRight: "6px",
        cursor: "default",
        ...(isOutlined && { display: "flex", gap: "5px" }),
      }}
    >
      {isOutlined ? (
        <>
          <span
            aria-hidden
            data-enum-dot=""
            style={{
              flexShrink: 0,
              alignSelf: "center",
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: borderColor,
            }}
          />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {label}
          </span>
          {endDecorator}
        </>
      ) : (
        <>
          {label}
          {endDecorator}
        </>
      )}
    </div>
  ) : null;

  return (
    <div
      style={{
        // ...getDefaultCellStyle("enum", theme),
        ...style,
      }}
    >
      {badgeContent}
    </div>
  );
}

function EnumsValueInteractive<T extends FieldDefinitionEnumsType<T>>({
  value,
  valueOptions,
  style,
  events,
  size = "md",
  isBorderVisible = true,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionEnumsType<T>> & {
  isBorderVisible?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const { colorScheme } = useColorScheme();
  const paletteMode = enumPaletteMode(colorScheme);
  const values = value || [];
  const colorMap = valueOptions?.getColors?.(paletteMode);
  const enumOptions = (valueOptions?.enum || []) as EnumOption[];
  const badgeRef = useRef<HTMLDivElement>(null);
  const [isPopperOpen, setIsPopperOpenState] = useState(false);

  // Manage transient state locally
  const [transientValues, setTransientValues] = useState<string[]>(values);

  const setIsPopperOpen = useCallback(
    (isOpen: boolean) => {
      setIsPopperOpenState(isOpen ?? false);
      if (!isOpen) {
        events?.onChangeField?.(transientValues);
        events?.onSubmitField?.();
      }
    },
    [events, transientValues],
  );

  // Sync transient state when value prop changes
  useEffect(() => {
    setTransientValues(values);
  }, [values]);

  const getIsChecked = useCallback(
    (option: EnumOption) => {
      return transientValues.includes(option.value);
    },
    [transientValues],
  );

  const handleItemClick = useCallback((option: EnumOption) => {
    setTransientValues((prev) => {
      const isCurrentlySelected = prev.includes(option.value);
      const newValues = isCurrentlySelected
        ? prev.filter((v) => v !== option.value)
        : [...prev, option.value];
      // Only update if the values differ
      if (
        newValues.length !== prev.length ||
        newValues.some((val, idx) => prev[idx] !== val)
      ) {
        return newValues;
      }
      return prev;
    });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        badgeRef.current?.focus();
        // Open the popper when focused
        setIsPopperOpen(true);
      },
      unsetFocus: () => {
        badgeRef.current?.blur();
        setIsPopperOpen(false);
      },
    }),
    [],
  );

  const badgeContent = (
    <div
      ref={badgeRef}
      tabIndex={0}
      role="button"
      data-testid={`field-enums-${transientValues.length > 0 ? transientValues.join("-") : "empty"}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsPopperOpen(true);
        }
      }}
      style={{
        display: "flex",
        flexDirection: "row",
        gap: "4px",
        flexWrap: "nowrap",
        ...style,
        outline: "none",
      }}
    >
      {transientValues.length > 0 ? (
        transientValues.map((val, index) => {
          const { color, backgroundColor, borderColor } = colorMap?.[val] ?? {
            color: EnumColors.GREY_1[paletteMode].color,
            backgroundColor: EnumColors.GREY_1[paletteMode].backgroundColor,
            borderColor: EnumColors.GREY_1[paletteMode].borderColor,
          };
          const text = typeof val === "string" ? val.replace(/_/g, " ") : val;
          return (
            <div
              key={index}
              data-testid={`field-enums-value-${val}`}
              style={{
                paddingTop: "1px",
                paddingBottom: "1px",
                ...((typographyStyles as Record<string, React.CSSProperties>)[
                  `body-${size}`
                ] as React.CSSProperties),
                color: color,
                border: isBorderVisible
                  ? `1px solid ${borderColor}`
                  : "1px solid transparent",
                backgroundColor: backgroundColor,
                borderRadius: "calc(var(--radius) - 4px)",
                maxWidth: "100%",
                width: "fit-content",
                overflow: "hidden",
                textOverflow: "ellipsis",
                alignItems: "baseline",
                flexWrap: "nowrap",
                textWrap: "nowrap",
                paddingLeft: "6px",
                paddingRight: "6px",
                cursor: "pointer",
              }}
            >
              {typeof text === "string" && !valueOptions?.keepsCase
                ? _.capitalize(text)
                : text}
            </div>
          );
        })
      ) : (
        <div
          style={{
            paddingTop: "1px",
            paddingBottom: "1px",
            ...((typographyStyles as Record<string, React.CSSProperties>)[
              `body-${size}`
            ] as React.CSSProperties),
            color:
              "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            border: isBorderVisible
              ? `1px solid ${"var(--border)"}`
              : "1px solid transparent",
            backgroundColor: "transparent",
            borderRadius: "calc(var(--radius) - 4px)",
            paddingLeft: "6px",
            paddingRight: "6px",
            cursor: "pointer",
            fontStyle: "italic",
          }}
        >
          Select options...
        </div>
      )}
    </div>
  );

  return (
    <Popper
      anchor={badgeContent}
      placement="bottom-start"
      isClickable={true}
      isHoverable={false}
      isOpenState={[isPopperOpen, setIsPopperOpen]}
      // A block, as the chips read alone are: inline, it sat on a line of the
      // page's own taller text, and was taller to pick than to read.
      style={{ display: "block" }}
      floatingStyle={{
        maxHeight: "400px",
        overflowY: "auto",
      }}
    >
      <List<EnumOption>
        data={enumOptions}
        getRowKey={(option) => option.value}
        getIsChecked={getIsChecked}
        isMultiselect={true}
        isCheckboxesEnabled={true}
        isNullable={true}
        onSelectMultiSelectOption={handleItemClick}
        size="sm"
      >
        <List.Items<EnumOption>
          children={(option: EnumOption) => (
            <List.Item
              value={option}
              data-testid={`field-enums-option-${option.value}`}
            >
              <div
                style={{
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  ...((typographyStyles as Record<string, React.CSSProperties>)[
                    `body-${size}`
                  ] as React.CSSProperties),
                  color:
                    colorMap?.[option.value]?.color ||
                    EnumColors.GREY_1[paletteMode].color,
                  border: `1px solid ${colorMap?.[option.value]?.borderColor || EnumColors.GREY_1[paletteMode].borderColor}`,
                  backgroundColor:
                    colorMap?.[option.value]?.backgroundColor ||
                    EnumColors.GREY_1[paletteMode].backgroundColor,
                  borderRadius: "calc(var(--radius) - 4px)",
                  paddingLeft: "6px",
                  paddingRight: "6px",
                  // As wide as its word, like the chip it becomes.
                  width: "fit-content",
                }}
              >
                {typeof option.value === "string"
                  ? valueOptions?.keepsCase
                    ? option.value
                    : _.capitalize(option.value.replace(/_/g, " "))
                  : option.value}
              </div>
            </List.Item>
          )}
        />
      </List>
    </Popper>
  );
}

function EnumsValue<T extends FieldDefinitionEnumsType<T>>({
  value,
  valueOptions,
  style,
  events,
  size = "md",
  isBorderVisible = true,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionEnumsType<T>> & {
  isBorderVisible?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const values = value || [];

  // If events are passed, use interactive component
  if (events) {
    return (
      <EnumsValueInteractive
        value={value}
        valueOptions={valueOptions}
        style={style}
        events={events}
        size={size}
        isBorderVisible={isBorderVisible}
        ref={ref}
      />
    );
  }

  return (
    <div
      style={{
        ...style,
        display: "flex",
        flexDirection: "row",
        gap: "4px",
      }}
    >
      {values.map((value, index) => (
        <FieldEnumValue
          key={index}
          value={value}
          valueOptions={valueOptions}
          size={size}
          isMultiline={false}
          isBorderVisible={isBorderVisible}
          style={{ paddingLeft: 0, paddingRight: 0 }}
        />
      ))}
    </div>
  );
}

function FieldEnumSkeleton({ size = "md" }: { size?: FieldSizeType }) {
  return (
    <Skeleton
      variant="text"
      level={`body-${size}`}
      style={{
        // ...getDefaultCellStyle("enum", theme),
        display: undefined,
        width: "75%",
        maxWidth: Values.DEFAULT_COLUMN_WIDTH,
      }}
    />
  );
}

function EnumsSkeleton({ size = "md" }: { size?: FieldSizeType }) {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        gap: "4px",
      }}
    >
      <Skeleton
        variant="text"
        level={`body-${size}`}
        style={{
          width: `min(33%, calc(${(Values.DEFAULT_COLUMN_WIDTH * 3) / 4}px + ${"4px"}))`,
          maxWidth: `calc(50% - ${"4px"})`,
        }}
      />
      <Skeleton
        variant="text"
        level={`body-${size}`}
        style={{
          width: `min(33%, calc(${(Values.DEFAULT_COLUMN_WIDTH * 3) / 4}px + ${"4px"}))`,
          maxWidth: `calc(50% - ${"4px"})`,
        }}
      />
    </div>
  );
}

export function FieldEnum({
  isLoading,
  isBorderVisible = false,
  readOnly = false,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionEnumType<any>> & {
  isLoading?: boolean;
  isBorderVisible?: boolean;
  readOnly?: boolean;
  /** How the chip is drawn while it is only read (``FieldEnumVariant``). */
  variant?: FieldEnumVariant;
  /** After its word, inside the chip, while it is only read. */
  endDecorator?: React.ReactNode;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const internalRef = useRef<FieldImperativeHandle>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        internalRef.current?.setFocus();
      },
      unsetFocus: () => {
        internalRef.current?.unsetFocus();
      },
    }),
    [],
  );

  if (isLoading) {
    return <FieldEnumSkeleton size={props.size} />;
  }

  return (
    <FieldEnumValue
      {...props}
      isBorderVisible={isBorderVisible}
      readOnly={readOnly}
      ref={internalRef}
    />
  );
}

export function Enums<T extends FieldDefinitionEnumsType<T>>({
  isLoading,
  isBorderVisible = false,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionEnumsType<T>> & {
  isBorderVisible?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const internalRef = useRef<FieldImperativeHandle>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        internalRef.current?.setFocus();
      },
      unsetFocus: () => {
        internalRef.current?.unsetFocus();
      },
    }),
    [],
  );

  if (isLoading) {
    return <EnumsSkeleton size={props.size} />;
  }

  return (
    <EnumsValue
      {...props}
      isBorderVisible={isBorderVisible}
      ref={internalRef}
    />
  );
}
